import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./App.css";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./AuthContext";
import LoginForm from "./components/LoginForm";

import { AdminPanel } from "./components/AdminPanel";
import { ReportsSection } from "./components/ReportsSection";
import { VisitManagement } from "./components/VisitManagement";
import { HelpDialog } from "./components/HelpDialog";
import { QCDashboard } from "./components/QCDashboard";
import { OpsDashboard } from "./components/OpsDashboard";
import { AppHeader } from "./components/AppHeader";
import { AppDialogs } from "./components/AppDialogs";
import { DashboardView } from "./components/dashboard/DashboardView";
import { ApplyTemplateDialog, NewTaskDialog } from "./components/dashboard/DashboardActions";
import { API } from "./utils/helpers";

const EMPTY_NEW_TASK = {
  title: '', description: '', assignee: '', planned_date: '', sliding_date: '',
  priority: 'medium', study_id: '', site_id: '', visit: '',
};
const EMPTY_TEMPLATE_FORM = { templateId: '', studyId: '', siteId: '', dayZeroDate: '', visit: '' };

function MainApp() {
  const { user, logout, isAdmin } = useAuth();

  // Core data
  const [tasks, setTasks] = useState([]);
  const [sites, setSites] = useState([]);
  const [studies, setStudies] = useState([]);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [visits, setVisits] = useState([]);
  const [failedEmails, setFailedEmails] = useState([]);
  const [pendingVisits, setPendingVisits] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("kanban");
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [filters, setFilters] = useState({ site: "", study: "", assignee: "", status: "", resource: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Dialog state
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const [isApplyTemplateOpen, setIsApplyTemplateOpen] = useState(false);
  const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isAddSiteOpen, setIsAddSiteOpen] = useState(false);
  const [isEditSiteOpen, setIsEditSiteOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isAddStudyOpen, setIsAddStudyOpen] = useState(false);
  const [isEditStudyOpen, setIsEditStudyOpen] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [isAddTemplateOpen, setIsAddTemplateOpen] = useState(false);
  const [isEditTemplateOpen, setIsEditTemplateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isAddVisitOpen, setIsAddVisitOpen] = useState(false);
  const [isEditVisitOpen, setIsEditVisitOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [isPendingVisitOpen, setIsPendingVisitOpen] = useState(false);
  const [selectedPendingVisit, setSelectedPendingVisit] = useState(null);

  // Forms
  const [newTaskData, setNewTaskData] = useState(EMPTY_NEW_TASK);
  const [templateFormData, setTemplateFormData] = useState(EMPTY_TEMPLATE_FORM);
  const [templateFormOptions, setTemplateFormOptions] = useState({ availableSites: [], availableStudies: [] });
  const [searchCache, setSearchCache] = useState({ sites: [], studies: [], users: [] });

  // ─── Initial load ─────────────────────────────
  const initializeData = useCallback(async () => {
    try {
      setLoading(true);
      const [tRes, siRes, stRes, uRes, tplRes, vRes, feRes, pvRes] = await Promise.all([
        axios.get(`${API}/tasks`), axios.get(`${API}/sites`), axios.get(`${API}/studies`),
        axios.get(`${API}/users`), axios.get(`${API}/templates`), axios.get(`${API}/visits`),
        axios.get(`${API}/failed-emails`), axios.get(`${API}/pending-visits`),
      ]);
      setTasks(tRes.data);
      setSites(siRes.data);
      setStudies(stRes.data);
      setUsers(uRes.data);
      setTemplates(tplRes.data);
      setVisits(vRes.data);
      setFailedEmails(feRes.data);
      setPendingVisits(pvRes.data);
      toast.success("Data loaded successfully!");
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { initializeData(); }, [initializeData]);

  // ─── Filtering ────────────────────────────────
  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    if (filters.site && filters.site !== "all") filtered = filtered.filter(t => t.site_id === filters.site);
    if (filters.study && filters.study !== "all") filtered = filtered.filter(t => t.study_id === filters.study);
    if (filters.assignee && filters.assignee !== "all") filtered = filtered.filter(t => t.assignee === filters.assignee);
    if (filters.status && filters.status !== "all") filtered = filtered.filter(t => t.status === filters.status);
    if (filters.resource && filters.resource !== "all") {
      filtered = filtered.filter(t => t.assignee === filters.resource || t.assignee.toLowerCase().includes(filters.resource.toLowerCase()));
    }
    if (searchTerm) {
      filtered = filtered.filter(t => t.title.toLowerCase().includes(searchTerm.toLowerCase()) || t.description.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return filtered;
  }, [tasks, filters, searchTerm]);

  // ─── Task handlers ────────────────────────────
  const openTaskEditor = (task) => { setSelectedTask(task); setIsEditTaskOpen(true); };
  const closeTaskEditor = () => { setSelectedTask(null); setIsEditTaskOpen(false); };

  const refreshTasks = async () => {
    const res = await axios.get(`${API}/tasks`);
    setTasks(res.data);
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      await refreshTasks();
      toast.success("Task deleted successfully");
    } catch { toast.error("Failed to delete task"); }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus });
      await refreshTasks();
      toast.success(newStatus === 'completed' ? "Task completed successfully" : "Task status updated");
    } catch { toast.error("Failed to update task status"); }
  };

  const updateTask = async (taskId, updateData) => {
    try {
      const response = await axios.put(`${API}/tasks/${taskId}`, updateData);
      setTasks(tasks.map(t => t.id === taskId ? response.data : t));
      toast.success("Task updated successfully!");
      closeTaskEditor();
    } catch { toast.error("Failed to update task"); }
  };

  const createTask = async (taskData) => {
    try {
      const response = await axios.post(`${API}/tasks`, taskData);
      setTasks([...tasks, response.data]);
      toast.success("Task created successfully!");
      setIsCreateTaskOpen(false);
      setNewTaskData(EMPTY_NEW_TASK);
    } catch { toast.error("Failed to create task"); }
  };

  const handleNewTaskSubmit = (e) => {
    e.preventDefault();
    if (!newTaskData.title || !newTaskData.assignee || !newTaskData.planned_date || !newTaskData.study_id || !newTaskData.site_id) {
      toast.error("Please fill in all required fields"); return;
    }
    const taskData = {
      ...newTaskData,
      planned_date: new Date(newTaskData.planned_date).toISOString(),
      sliding_date: newTaskData.sliding_date ? new Date(newTaskData.sliding_date).toISOString() : new Date(newTaskData.planned_date).toISOString(),
    };
    createTask(taskData);
  };

  // ─── User CRUD ────────────────────────────────
  const createUser = async (userData) => {
    try {
      const response = await axios.post(`${API}/users`, userData);
      setUsers([...users, response.data]);
      toast.success("User created successfully!");
      setIsAddUserOpen(false);
    } catch { toast.error("Failed to create user"); }
  };
  const updateUser = async (userId, updateData) => {
    try {
      const response = await axios.put(`${API}/users/${userId}`, updateData);
      setUsers(users.map(u => u.id === userId ? response.data : u));
      toast.success("User updated successfully!");
      setIsEditUserOpen(false); setSelectedUser(null);
    } catch { toast.error("Failed to update user"); }
  };
  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      toast.success("User deleted successfully!");
    } catch { toast.error("Failed to delete user"); }
  };
  const openUserEditor = (u) => { setSelectedUser(u); setIsEditUserOpen(true); };

  // ─── Site CRUD ────────────────────────────────
  const createSite = async (siteData) => {
    try {
      const response = await axios.post(`${API}/sites`, siteData);
      setSites([...sites, response.data]);
      toast.success("Site created successfully!");
      setIsAddSiteOpen(false);
    } catch { toast.error("Failed to create site"); }
  };
  const updateSite = async (siteId, updateData) => {
    try {
      const response = await axios.put(`${API}/sites/${siteId}`, updateData);
      setSites(sites.map(s => s.id === siteId ? response.data : s));
      toast.success("Site updated successfully!");
      setIsEditSiteOpen(false); setSelectedSite(null);
    } catch { toast.error("Failed to update site"); }
  };
  const deleteSite = async (siteId) => {
    if (!window.confirm("Are you sure you want to delete this site?")) return;
    try {
      await axios.delete(`${API}/sites/${siteId}`);
      setSites(sites.filter(s => s.id !== siteId));
      toast.success("Site deleted successfully!");
    } catch { toast.error("Failed to delete site"); }
  };
  const openSiteEditor = (s) => { setSelectedSite(s); setIsEditSiteOpen(true); };

  // ─── Study CRUD ───────────────────────────────
  const createStudy = async (studyData) => {
    try {
      const response = await axios.post(`${API}/studies`, studyData);
      setStudies([...studies, response.data]);
      toast.success("Study created successfully!");
      setIsAddStudyOpen(false);
    } catch { toast.error("Failed to create study"); }
  };
  const updateStudy = async (studyId, updateData) => {
    try {
      const response = await axios.put(`${API}/studies/${studyId}`, updateData);
      setStudies(studies.map(s => s.id === studyId ? response.data : s));
      toast.success("Study updated successfully!");
      setIsEditStudyOpen(false); setSelectedStudy(null);
    } catch { toast.error("Failed to update study"); }
  };
  const deleteStudy = async (studyId) => {
    if (!window.confirm("Are you sure you want to delete this study?")) return;
    try {
      await axios.delete(`${API}/studies/${studyId}`);
      setStudies(studies.filter(s => s.id !== studyId));
      toast.success("Study deleted successfully!");
    } catch { toast.error("Failed to delete study"); }
  };
  const openStudyEditor = (s) => { setSelectedStudy(s); setIsEditStudyOpen(true); };

  // ─── Template CRUD ────────────────────────────
  const createTemplate = async (templateData) => {
    try {
      const response = await axios.post(`${API}/templates`, templateData);
      setTemplates([...templates, response.data]);
      toast.success("Template created successfully!");
      setIsAddTemplateOpen(false);
    } catch { toast.error("Failed to create template"); }
  };
  const updateTemplate = async (templateId, updateData) => {
    try {
      const response = await axios.put(`${API}/templates/${templateId}`, updateData);
      setTemplates(templates.map(t => t.id === templateId ? response.data : t));
      toast.success("Template updated successfully!");
      setIsEditTemplateOpen(false); setSelectedTemplate(null);
    } catch { toast.error("Failed to update template"); }
  };
  const deleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      setTemplates(templates.filter(t => t.id !== templateId));
      toast.success("Template deleted successfully!");
    } catch { toast.error("Failed to delete template"); }
  };
  const openTemplateEditor = (t) => { setSelectedTemplate(t); setIsEditTemplateOpen(true); };

  // ─── Search & Auto-populate ───────────────────
  const searchSites = async (query = "") => {
    if (query.length < 2 && searchCache.sites.length > 0) return searchCache.sites;
    try {
      const response = await axios.get(`${API}/search/sites`, { params: { q: query } });
      if (query.length < 2) setSearchCache(prev => ({ ...prev, sites: response.data }));
      return response.data;
    } catch { return sites; }
  };

  const searchStudies = async (query = "") => {
    if (query.length < 2 && searchCache.studies.length > 0) return searchCache.studies;
    try {
      const response = await axios.get(`${API}/search/studies`, { params: { q: query } });
      if (query.length < 2) setSearchCache(prev => ({ ...prev, studies: response.data }));
      return response.data;
    } catch { return studies; }
  };

  const getStudyAutoPopulateData = async (studyId) => {
    try { return (await axios.get(`${API}/auto-populate/study-details/${studyId}`)).data; }
    catch { return null; }
  };
  const getSiteAutoPopulateData = async (siteId) => {
    try { return (await axios.get(`${API}/auto-populate/site-details/${siteId}`)).data; }
    catch { return null; }
  };

  const handleTemplateStudyChange = async (studyId) => {
    setTemplateFormData(prev => ({ ...prev, studyId }));
    try {
      const autoData = await getStudyAutoPopulateData(studyId);
      if (autoData?.related_sites) {
        setTemplateFormOptions(prev => ({ ...prev, availableSites: autoData.related_sites }));
        if (autoData.related_sites.length === 1) setTemplateFormData(prev => ({ ...prev, siteId: autoData.related_sites[0].id }));
        toast.success(`Found ${autoData.related_sites.length} related sites for ${autoData.study_name}`);
      }
    } catch { setTemplateFormOptions(prev => ({ ...prev, availableSites: sites })); }
  };

  const handleTemplateSiteChange = async (siteId) => {
    setTemplateFormData(prev => ({ ...prev, siteId }));
    try {
      const autoData = await getSiteAutoPopulateData(siteId);
      if (autoData?.related_studies) {
        setTemplateFormOptions(prev => ({ ...prev, availableStudies: autoData.related_studies }));
        if (autoData.related_studies.length === 1) setTemplateFormData(prev => ({ ...prev, studyId: autoData.related_studies[0].id }));
        toast.success(`Found ${autoData.related_studies.length} related studies for ${autoData.site_name}`);
      }
    } catch { setTemplateFormOptions(prev => ({ ...prev, availableStudies: studies })); }
  };

  useEffect(() => {
    if (isApplyTemplateOpen) setTemplateFormOptions({ availableSites: sites, availableStudies: studies });
  }, [isApplyTemplateOpen, sites, studies]);

  const applyTemplate = async () => {
    try {
      if (!templateFormData.templateId || !templateFormData.studyId || !templateFormData.siteId || !templateFormData.dayZeroDate) {
        toast.error("Please fill in all fields"); return;
      }
      const response = await axios.post(`${API}/templates/apply`, {
        template_id: templateFormData.templateId,
        study_id: templateFormData.studyId,
        site_id: templateFormData.siteId,
        day_zero_date: templateFormData.dayZeroDate,
        visit: templateFormData.visit,
      });
      await refreshTasks();
      toast.success(`Applied template: ${response.data.message}`);
      setIsApplyTemplateOpen(false);
      setTemplateFormData(EMPTY_TEMPLATE_FORM);
    } catch { toast.error("Failed to apply template"); }
  };

  // ─── Render ───────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading Clinical Research Dashboard...</p>
        </div>
      </div>
    );
  }

  const taskCardProps = {
    onClick: openTaskEditor, sites, studies, tasks, setTasks,
    updateTaskStatus, confirmDeleteTask: deleteTask,
  };

  const headerActions = currentPage === "dashboard" ? (
    <div className="flex items-center space-x-4">
      <ApplyTemplateDialog
        isOpen={isApplyTemplateOpen}
        setIsOpen={setIsApplyTemplateOpen}
        templates={templates}
        templateFormData={templateFormData}
        setTemplateFormData={setTemplateFormData}
        templateFormOptions={templateFormOptions}
        handleTemplateStudyChange={handleTemplateStudyChange}
        handleTemplateSiteChange={handleTemplateSiteChange}
        applyTemplate={applyTemplate}
      />
      <NewTaskDialog
        isOpen={isCreateTaskOpen}
        setIsOpen={setIsCreateTaskOpen}
        newTaskData={newTaskData}
        setNewTaskData={setNewTaskData}
        sites={sites}
        studies={studies}
        users={users}
        handleNewTaskSubmit={handleNewTaskSubmit}
      />
    </div>
  ) : null;

  const renderPage = () => {
    if (currentPage === "admin") {
      return (
        <AdminPanel
          users={users} sites={sites} studies={studies} templates={templates}
          setIsAddUserOpen={setIsAddUserOpen} setIsAddSiteOpen={setIsAddSiteOpen}
          setIsAddStudyOpen={setIsAddStudyOpen} setIsAddTemplateOpen={setIsAddTemplateOpen}
          openUserEditor={openUserEditor} openSiteEditor={openSiteEditor}
          openStudyEditor={openStudyEditor} openTemplateEditor={openTemplateEditor}
          deleteUser={deleteUser} deleteSite={deleteSite}
          deleteStudy={deleteStudy} deleteTemplate={deleteTemplate}
        />
      );
    }
    if (currentPage === "visits") {
      return (
        <VisitManagement
          visits={visits} setVisits={setVisits} sites={sites} setSites={setSites}
          studies={studies} setStudies={setStudies} failedEmails={failedEmails}
          setFailedEmails={setFailedEmails} pendingVisits={pendingVisits}
          setPendingVisits={setPendingVisits}
          isAddVisitOpen={isAddVisitOpen} setIsAddVisitOpen={setIsAddVisitOpen}
          isEditVisitOpen={isEditVisitOpen} setIsEditVisitOpen={setIsEditVisitOpen}
          selectedVisit={selectedVisit} setSelectedVisit={setSelectedVisit}
          isPendingVisitOpen={isPendingVisitOpen} setIsPendingVisitOpen={setIsPendingVisitOpen}
          selectedPendingVisit={selectedPendingVisit} setSelectedPendingVisit={setSelectedPendingVisit}
          searchSites={searchSites} searchStudies={searchStudies}
          getStudyAutoPopulateData={getStudyAutoPopulateData}
          getSiteAutoPopulateData={getSiteAutoPopulateData}
        />
      );
    }
    if (currentPage === "reports") {
      return <ReportsSection visits={visits} users={users} sites={sites} studies={studies} tasks={tasks} />;
    }
    if (currentPage === "qc") {
      return <QCDashboard sites={sites} studies={studies} />;
    }
    if (currentPage === "ops") {
      return <OpsDashboard />;
    }
    return (
      <DashboardView
        tasks={tasks}
        filteredTasks={filteredTasks}
        filters={filters}
        setFilters={setFilters}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        viewMode={viewMode}
        setViewMode={setViewMode}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        sites={sites}
        studies={studies}
        users={users}
        taskCardProps={taskCardProps}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <AppHeader
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        user={user}
        isAdmin={isAdmin}
        logout={logout}
        onHelpClick={() => setIsHelpOpen(true)}
        headerActions={headerActions}
      />

      <HelpDialog isOpen={isHelpOpen} onClose={setIsHelpOpen} />

      <AppDialogs
        isEditTaskOpen={isEditTaskOpen} setIsEditTaskOpen={setIsEditTaskOpen}
        selectedTask={selectedTask} updateTask={updateTask} closeTaskEditor={closeTaskEditor}
        isAddUserOpen={isAddUserOpen} setIsAddUserOpen={setIsAddUserOpen}
        isEditUserOpen={isEditUserOpen} setIsEditUserOpen={setIsEditUserOpen}
        selectedUser={selectedUser} setSelectedUser={setSelectedUser}
        createUser={createUser} updateUser={updateUser}
        isAddSiteOpen={isAddSiteOpen} setIsAddSiteOpen={setIsAddSiteOpen}
        isEditSiteOpen={isEditSiteOpen} setIsEditSiteOpen={setIsEditSiteOpen}
        selectedSite={selectedSite} setSelectedSite={setSelectedSite}
        createSite={createSite} updateSite={updateSite}
        isAddStudyOpen={isAddStudyOpen} setIsAddStudyOpen={setIsAddStudyOpen}
        isEditStudyOpen={isEditStudyOpen} setIsEditStudyOpen={setIsEditStudyOpen}
        selectedStudy={selectedStudy} setSelectedStudy={setSelectedStudy}
        createStudy={createStudy} updateStudy={updateStudy}
        isAddTemplateOpen={isAddTemplateOpen} setIsAddTemplateOpen={setIsAddTemplateOpen}
        isEditTemplateOpen={isEditTemplateOpen} setIsEditTemplateOpen={setIsEditTemplateOpen}
        selectedTemplate={selectedTemplate} setSelectedTemplate={setSelectedTemplate}
        createTemplate={createTemplate} updateTemplate={updateTemplate}
        sites={sites} studies={studies} users={users}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderPage()}
      </main>
    </div>
  );
}

function App() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <LoginForm />;
  return <MainApp />;
}

function AppWrapper() {
  return (
    <AuthProvider>
      <Toaster richColors position="top-right" />
      <App />
    </AuthProvider>
  );
}

export default AppWrapper;
